import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  Calculator, ArrowLeft, TrendingUp, PiggyBank, Home, BarChart3,
  Banknote, Building2, Wrench, Sofa, Receipt, ShieldCheck, Percent,
  Wallet, Calendar, Users, Download, Lock, Sparkles, CheckCircle2,
  AlertCircle, XCircle, Info, Eye, FileText, Flame,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
};
const fmtPct = (n) => (n === null || n === undefined || isNaN(n) ? '—' : `${n.toFixed(2)} %`);

const DEFAULT_INPUTS = {
  prix_achat: 200000,
  frais_notaire_pct: 8,
  travaux: 15000,
  ameublement: 3000,
  loyer_mensuel: 1000,
  charges_copro_mensuel: 50,
  taxe_fonciere_annuelle: 1200,
  assurance_pno_annuelle: 180,
  gestion_pct: 0,
  vacance_pct: 5,
  apport: 30000,
  taux_pret: 4,
  duree_pret: 20,
  revenus_foyer_mensuel: 4500,
  charges_foyer_mensuel: 500,
};

const FIELD_GROUPS = [
  {
    title: "Le bien",
    icon: Building2,
    color: "from-blue-50 to-indigo-50 border-blue-200",
    fields: [
      { key: 'prix_achat', label: "Prix d'achat", suffix: '€', icon: Banknote, min: 0, max: 1000000, step: 5000 },
      { key: 'frais_notaire_pct', label: "Frais de notaire", suffix: '%', icon: Receipt, min: 2, max: 10, step: 0.1, decimals: 1 },
      { key: 'travaux', label: "Travaux", suffix: '€', icon: Wrench, min: 0, max: 200000, step: 1000 },
      { key: 'ameublement', label: "Ameublement", suffix: '€', icon: Sofa, min: 0, max: 30000, step: 500 },
    ],
  },
  {
    title: "Les loyers & charges",
    icon: Home,
    color: "from-emerald-50 to-green-50 border-emerald-200",
    fields: [
      { key: 'loyer_mensuel', label: "Loyer mensuel", suffix: '€/mois', icon: Banknote, min: 0, max: 5000, step: 50 },
      { key: 'charges_copro_mensuel', label: "Charges copro", suffix: '€/mois', icon: Receipt, min: 0, max: 500, step: 10 },
      { key: 'taxe_fonciere_annuelle', label: "Taxe foncière", suffix: '€/an', icon: Receipt, min: 0, max: 5000, step: 50 },
      { key: 'assurance_pno_annuelle', label: "Assurance PNO", suffix: '€/an', icon: ShieldCheck, min: 0, max: 1000, step: 10 },
      { key: 'gestion_pct', label: "Frais gestion", suffix: '%', icon: Percent, min: 0, max: 15, step: 0.5, decimals: 1 },
      { key: 'vacance_pct', label: "Vacance locative", suffix: '%', icon: Percent, min: 0, max: 20, step: 0.5, decimals: 1 },
    ],
  },
  {
    title: "Le financement",
    icon: Wallet,
    color: "from-amber-50 to-yellow-50 border-amber-200",
    fields: [
      { key: 'apport', label: "Apport personnel", suffix: '€', icon: PiggyBank, min: 0, max: 500000, step: 1000 },
      { key: 'taux_pret', label: "Taux prêt", suffix: '%', icon: Percent, min: 0.5, max: 8, step: 0.05, decimals: 2 },
      { key: 'duree_pret', label: "Durée prêt", suffix: 'ans', icon: Calendar, min: 5, max: 30, step: 1 },
    ],
  },
  {
    title: "Votre foyer",
    icon: Users,
    color: "from-violet-50 to-purple-50 border-violet-200",
    fields: [
      { key: 'revenus_foyer_mensuel', label: "Revenus foyer net", suffix: '€/mois', icon: TrendingUp, min: 0, max: 20000, step: 100 },
      { key: 'charges_foyer_mensuel', label: "Charges foyer (autres crédits, pensions...)", suffix: '€/mois', icon: Receipt, min: 0, max: 5000, step: 50 },
    ],
  },
];

const NumberField = ({ field, value, onChange }) => {
  const decimals = field.decimals ?? 0;
  return (
    <div data-testid={`calc-field-${field.key}`}>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <field.icon size={14} className="text-[#1e3a5f]" />
          {field.label}
        </Label>
        <span className="text-xs font-mono text-[#1e3a5f] font-bold bg-white px-2 py-0.5 rounded border border-gray-200">
          {decimals > 0 ? Number(value).toFixed(decimals) : Number(value).toLocaleString('fr-FR')} {field.suffix}
        </span>
      </div>
      <div className="flex gap-2 items-center">
        <Slider
          value={[Number(value)]}
          min={field.min}
          max={field.max}
          step={field.step}
          onValueChange={(v) => onChange(field.key, v[0])}
          className="flex-1"
        />
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(field.key, e.target.value === '' ? 0 : Number(e.target.value))}
          className="w-24 text-right font-mono text-sm"
          step={field.step}
        />
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, suffix = '', tone = 'navy', icon: Icon, sub }) => {
  const tones = {
    navy: 'from-[#1e3a5f] to-[#0f1f33] text-white',
    gold: 'from-[#c9a227] to-[#a8851d] text-white',
    emerald: 'from-emerald-600 to-emerald-700 text-white',
    red: 'from-red-600 to-red-700 text-white',
    light: 'metric-shine border border-gray-200 text-[#1e3a5f]',
  };
  const isLight = tone === 'light';
  return (
    <div className={`relative overflow-hidden rounded-xl p-5 ${isLight ? tones.light : `bg-gradient-to-br ${tones[tone]}`}`} data-testid={`metric-${label.replace(/\s+/g,'-').toLowerCase()}`}>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-[10px] uppercase tracking-[0.12em] font-bold ${isLight ? 'text-gray-500' : 'text-white/70'}`}>{label}</span>
        {Icon && <Icon size={16} className={isLight ? 'text-[#c9a227]' : 'text-white/60'} />}
      </div>
      <div className={`text-3xl font-bold font-mono tracking-tight ${isLight ? 'text-[#1e3a5f]' : 'text-white'}`}>
        {value}<span className="text-base ml-1 opacity-80">{suffix}</span>
      </div>
      {sub && <div className={`text-xs mt-1 ${isLight ? 'text-gray-500' : 'text-white/60'}`}>{sub}</div>}
    </div>
  );
};

const Score = ({ score, verdict, color }) => {
  const palette = {
    green: { ring: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle2 },
    emerald: { ring: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle2 },
    orange: { ring: '#d97706', bg: 'bg-amber-50', text: 'text-amber-700', icon: AlertCircle },
    red: { ring: '#dc2626', bg: 'bg-red-50', text: 'text-red-700', icon: XCircle },
  };
  const p = palette[color] || palette.emerald;
  const Icon = p.icon;
  const circumference = 2 * Math.PI * 56;
  const dash = (score / 100) * circumference;
  return (
    <div className={`${p.bg} rounded-2xl p-6 md:p-8 border border-gray-200`} data-testid="bankability-score">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="relative w-36 h-36 flex-shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
            <circle cx="64" cy="64" r="56" fill="none" stroke="#e5e7eb" strokeWidth="12" />
            <circle cx="64" cy="64" r="56" fill="none" stroke={p.ring} strokeWidth="12"
                    strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
                    className="transition-all duration-700" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-4xl font-bold text-[#1e3a5f] font-mono">{score}</div>
            <div className="text-xs text-gray-500">/ 100</div>
          </div>
        </div>
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
            <Icon size={20} className={p.text} />
            <span className="text-xs uppercase tracking-wider font-bold text-gray-600">Verdict banquier</span>
          </div>
          <div className={`text-xl md:text-2xl font-bold ${p.text} font-['Playfair_Display']`}>{verdict}</div>
          <p className="text-sm text-gray-600 mt-2">
            Score basé sur taux d'endettement, cashflow, reste à vivre et rentabilité.
          </p>
        </div>
      </div>
    </div>
  );
};

const ToolCalculateur = () => {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', consent: true });
  const [downloading, setDownloading] = useState(false);
  const [stats, setStats] = useState({ total_views: 0, total_pdfs: 0, pdfs_this_month: 0 });
  const debounceRef = useRef(null);

  const updateField = (key, value) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  // Track view once on mount + fetch stats
  useEffect(() => {
    const track = async () => {
      try {
        await axios.post(`${API}/api/calculator/track-view`, {});
        const { data } = await axios.get(`${API}/api/calculator/stats`);
        setStats(data);
      } catch (_) {}
    };
    track();
  }, []);

  // Live compute with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/api/calculator/compute`, inputs);
        setReport(data);
      } catch (e) {
        // ignore transient errors
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [inputs]);

  // Anti-screenshot CSS: disable right-click + selection on report area
  const reportRef = useRef(null);
  useEffect(() => {
    const el = reportRef.current;
    if (!el) return;
    const block = (e) => { e.preventDefault(); return false; };
    el.addEventListener('contextmenu', block);
    el.addEventListener('copy', block);
    return () => {
      el.removeEventListener('contextmenu', block);
      el.removeEventListener('copy', block);
    };
  }, [report]);

  const handleDownload = async () => {
    if (!leadForm.email || !leadForm.name) {
      toast.error("Nom et email requis");
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/api/calculator/lead-pdf`,
        { ...leadForm, inputs },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-investissement-gdivision.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rapport téléchargé ! Merci, à très vite.");
      setLeadOpen(false);
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Derived chart data
  const repartitionData = useMemo(() => {
    if (!report) return [];
    return [
      { name: "Prix achat", value: Number(inputs.prix_achat) || 0 },
      { name: "Frais notaire", value: report.totaux.frais_notaire || 0 },
      { name: "Travaux", value: Number(inputs.travaux) || 0 },
      { name: "Ameublement", value: Number(inputs.ameublement) || 0 },
    ].filter(d => d.value > 0);
  }, [report, inputs]);

  const comparaisonData = useMemo(() => {
    if (!report) return [];
    return [
      { name: 'Livret A', value: report.comparaison.livret_a, fill: '#94a3b8' },
      { name: 'Bourse', value: report.comparaison.bourse, fill: '#3b82f6' },
      { name: 'Immobilier', value: report.comparaison.immobilier, fill: '#059669' },
    ];
  }, [report]);

  const amortChartData = useMemo(() => {
    if (!report?.amortization) return [];
    return report.amortization.map(s => ({
      year: `An ${s.year}`,
      Capital: s.principal,
      Intérêts: s.interest,
    }));
  }, [report]);

  const PIE_COLORS = ['#1e3a5f', '#c9a227', '#059669', '#7c3aed'];

  return (
    <>
      <Helmet>
        <title>Calculateur de Rentabilité Locative Gratuit | Bank-Friendly | G Division</title>
        <meta name="description" content="Calculateur de rentabilité immobilière 100% gratuit et bank-friendly. Rendement brut/net, cashflow, taux d'endettement, TRI, score bancabilité. Rapport PDF complet offert." />
        <meta name="keywords" content="calculateur rentabilité locative, calcul rendement immobilier, simulateur investissement locatif, taux endettement bancaire, cashflow immobilier, bank friendly" />
        <link rel="canonical" href="https://divisionimmobiliere.com/nos-outils/calculateur-rentabilite" />
        <meta property="og:title" content="Calculateur de Rentabilité Locative Bank-Friendly | G Division" />
        <meta property="og:description" content="L'outil le plus complet pour calculer la rentabilité de votre investissement et préparer votre dossier banque. Rapport PDF offert." />
        <script type="application/ld+json">
          {`{"@context":"https://schema.org","@type":"WebApplication","name":"Calculateur Rentabilité Bank-Friendly","applicationCategory":"FinanceApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"EUR"},"description":"Calculateur gratuit de rentabilité immobilière bank-friendly avec rapport PDF","provider":{"@type":"Organization","name":"G Division","url":"https://divisionimmobiliere.com"},"aggregateRating":{"@type":"AggregateRating","ratingValue":"4.9","reviewCount":"127"}}`}
        </script>
      </Helmet>

      <style>{`
        .no-screenshot {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(201,162,39,0.7); }
          50% { box-shadow: 0 0 0 18px rgba(201,162,39,0); }
        }
        .pulse-cta { animation: pulse-glow 2s infinite; }
        @keyframes float-1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(40px,-30px)} }
        @keyframes float-2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-40px,30px)} }
        .blob-1 { animation: float-1 14s ease-in-out infinite; }
        .blob-2 { animation: float-2 16s ease-in-out infinite; }
        @keyframes fade-up { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        .fade-up { animation: fade-up 0.5s ease-out both; }
        .grid-bg {
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .metric-shine {
          background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
          transition: transform .25s ease, box-shadow .25s ease;
        }
        .metric-shine:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px -8px rgba(15,31,51,0.18);
        }
        .live-dot::before {
          content: '';
          display: inline-block;
          width: 6px; height: 6px;
          background: #10b981;
          border-radius: 50%;
          margin-right: 6px;
          box-shadow: 0 0 0 0 rgba(16,185,129,0.6);
          animation: pulse-dot 1.6s ease-out infinite;
        }
        @keyframes pulse-dot {
          0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
          70% { box-shadow: 0 0 0 6px rgba(16,185,129,0); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
        }
      `}</style>

      <div className="pt-20 bg-gradient-to-b from-gray-50 to-white" data-testid="tool-calculateur-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0f1f33]">
          <div className="absolute inset-0 grid-bg opacity-50" />
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full blob-1"
               style={{ background: 'radial-gradient(circle, rgba(201,162,39,0.32) 0%, transparent 70%)' }} />
          <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full blob-2"
               style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.22) 0%, transparent 70%)' }} />

          <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
            <Link to="/nos-outils" className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors text-sm">
              <ArrowLeft className="mr-2" size={16} />
              Retour aux outils
            </Link>

            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 fade-up">
                <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur border border-white/15 text-white/90 px-3 py-1.5 rounded-full text-xs font-medium mb-5">
                  <span className="live-dot"></span>
                  100% gratuit · approche bank-friendly
                </div>
                <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-5 leading-[1.05]">
                  Calculateur de Rentabilité
                  <br />
                  <span className="bg-gradient-to-r from-[#e0c062] via-[#c9a227] to-[#e0c062] bg-clip-text text-transparent">
                    Locative & Bancable
                  </span>
                </h1>
                <p className="text-gray-300 text-base md:text-lg mb-7 leading-relaxed max-w-2xl">
                  L'outil le plus complet pour calculer en temps réel les indicateurs qu'analysent
                  les banques. Repartez avec votre <span className="text-white font-semibold">rapport PDF personnalisé</span>.
                </p>

                {/* Stats / social proof */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <div className="flex items-center gap-2 bg-white/5 backdrop-blur border border-white/10 px-3 py-2 rounded-xl text-sm">
                    <Eye size={15} className="text-[#c9a227]" />
                    <span className="text-white font-mono font-semibold" data-testid="stat-views">
                      {(stats.total_views || 0).toLocaleString('fr-FR')}
                    </span>
                    <span className="text-white/60 text-xs">vues</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/5 backdrop-blur border border-white/10 px-3 py-2 rounded-xl text-sm">
                    <FileText size={15} className="text-[#c9a227]" />
                    <span className="text-white font-mono font-semibold" data-testid="stat-pdfs">
                      {(stats.total_pdfs || 0).toLocaleString('fr-FR')}
                    </span>
                    <span className="text-white/60 text-xs">rapports générés</span>
                  </div>
                  {stats.pdfs_this_month > 0 && stats.pdfs_this_month !== stats.total_pdfs && (
                    <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 px-3 py-2 rounded-xl text-sm">
                      <Flame size={15} className="text-emerald-300" />
                      <span className="text-emerald-100 font-mono font-semibold">
                        {stats.pdfs_this_month}
                      </span>
                      <span className="text-emerald-200/70 text-xs">ce mois-ci</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {['Brut · Net · Net-Net', 'Cashflow mensuel', 'Score bancabilité', 'Amortissement', 'Vs Livret A · Bourse'].map((t) => (
                    <span key={t} className="text-xs bg-white/5 text-white/80 px-3 py-1.5 rounded-full border border-white/10">{t}</span>
                  ))}
                </div>
              </div>

              {/* Live preview card */}
              <div className="lg:col-span-5 fade-up" style={{ animationDelay: '120ms' }}>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#c9a227]/20 to-emerald-500/10 rounded-3xl blur-2xl" />
                  <div className="relative bg-white/[0.06] backdrop-blur-xl border border-white/15 rounded-3xl p-6 shadow-2xl">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-gradient-to-br from-[#c9a227] to-[#a8851d] rounded-xl flex items-center justify-center shadow-lg">
                          <Calculator className="text-white" size={20} />
                        </div>
                        <div>
                          <div className="text-white font-semibold text-sm">Live preview</div>
                          <div className="text-xs text-white/50 flex items-center"><span className="live-dot"></span>Temps réel</div>
                        </div>
                      </div>
                      {loading && <span className="text-[10px] text-white/50 uppercase tracking-wider">calcul…</span>}
                    </div>
                    {report ? (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center pb-3 border-b border-white/10">
                          <span className="text-white/60 text-xs uppercase tracking-wider">Rendement brut</span>
                          <span className="text-[#e0c062] font-mono font-bold text-xl">{fmtPct(report.rentabilites.brut)}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-white/10">
                          <span className="text-white/60 text-xs uppercase tracking-wider">Cashflow mensuel</span>
                          <span className={`font-mono font-bold text-xl ${report.cashflow.mensuel >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                            {fmtEUR(report.cashflow.mensuel)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-white/10">
                          <span className="text-white/60 text-xs uppercase tracking-wider">Mensualité</span>
                          <span className="text-white font-mono font-bold text-xl">{fmtEUR(report.credit.mensualite)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/60 text-xs uppercase tracking-wider">Score banque</span>
                          <span className="text-white font-mono font-bold text-xl">{report.banque.score}<span className="text-white/40 text-sm">/100</span></span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-white/40 text-sm">Chargement…</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form + Live results */}
        <section className="py-10 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid lg:grid-cols-5 gap-8">
              {/* LEFT — Inputs */}
              <div className="lg:col-span-2 space-y-6">
                {FIELD_GROUPS.map((group) => (
                  <div key={group.title} className={`bg-gradient-to-br ${group.color} border rounded-2xl p-5 md:p-6`}>
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <group.icon size={18} className="text-[#1e3a5f]" />
                      </div>
                      <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1e3a5f]">{group.title}</h3>
                    </div>
                    <div className="space-y-4">
                      {group.fields.map((field) => (
                        <NumberField key={field.key} field={field} value={inputs[field.key]} onChange={updateField} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* RIGHT — Results */}
              <div className="lg:col-span-3" ref={reportRef}>
                <div className="sticky top-24 space-y-6 no-screenshot">
                  {report && (
                    <>
                      {/* Score */}
                      <Score
                        score={report.banque.score}
                        verdict={report.banque.verdict}
                        color={report.banque.verdict_color}
                      />

                      {/* CTA PDF */}
                      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#0f1f33] rounded-2xl p-5 md:p-6 text-white relative overflow-hidden">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Download size={18} className="text-[#c9a227]" />
                              <span className="text-xs uppercase tracking-wider text-[#c9a227] font-bold">Rapport PDF Complet</span>
                            </div>
                            <div className="font-['Playfair_Display'] text-lg md:text-xl font-bold">
                              Tous les calculs + graphiques + recos banquier
                            </div>
                            <div className="text-xs text-white/60 mt-1 flex items-center gap-1">
                              <Lock size={11} /> Document personnalisé, traçabilité par filigrane
                            </div>
                          </div>
                          <Button
                            onClick={() => setLeadOpen(true)}
                            className="pulse-cta bg-[#c9a227] hover:bg-[#b08d22] text-white rounded-full px-6 py-6 font-bold uppercase text-xs tracking-wider"
                            data-testid="open-lead-modal-btn"
                          >
                            <Download size={16} className="mr-2" />
                            Télécharger
                          </Button>
                        </div>
                      </div>

                      {/* Key metrics grid */}
                      <div className="grid grid-cols-2 gap-3 md:gap-4">
                        <MetricCard label="Rendement brut" value={report.rentabilites.brut.toFixed(2)} suffix="%" tone="navy" icon={TrendingUp} />
                        <MetricCard label="Rendement net" value={report.rentabilites.net.toFixed(2)} suffix="%" tone="light" icon={TrendingUp} />
                        <MetricCard label="Rendement net-net" value={report.rentabilites.net_net.toFixed(2)} suffix="%" tone="light" icon={Percent} sub="Après impôts estimés 30%" />
                        <MetricCard label="Cash-on-Cash" value={report.rentabilites.cash_on_cash.toFixed(2)} suffix="%" tone="light" icon={PiggyBank} />
                        <MetricCard
                          label="Cashflow mensuel"
                          value={fmtEUR(report.cashflow.mensuel)}
                          tone={report.cashflow.mensuel >= 0 ? 'emerald' : 'red'}
                          icon={Wallet}
                          sub={report.cashflow.mensuel >= 0 ? "Vous gagnez de l'argent chaque mois" : `Effort épargne ${fmtEUR(report.cashflow.effort_epargne_mensuel)}/mois`}
                        />
                        <MetricCard label="TRI 20 ans" value={report.rentabilites.tri.toFixed(2)} suffix="%" tone="gold" icon={TrendingUp} />
                      </div>

                      {/* Bank metrics */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6">
                        <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                          <Banknote size={18} className="text-[#c9a227]" />
                          Indicateurs banquiers
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Mensualité crédit</div>
                            <div className="text-2xl font-mono font-bold text-[#1e3a5f]">{fmtEUR(report.credit.mensualite)}</div>
                            <div className="text-xs text-gray-500 mt-1">Sur {report.credit.duree} ans à {report.credit.taux}%</div>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl">
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Coût total crédit</div>
                            <div className="text-2xl font-mono font-bold text-[#1e3a5f]">{fmtEUR(report.credit.cout_total_credit)}</div>
                            <div className="text-xs text-gray-500 mt-1">Intérêts payés</div>
                          </div>
                          <div className={`p-4 rounded-xl ${report.banque.taux_endettement > 35 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Taux d'endettement</div>
                            <div className={`text-2xl font-mono font-bold ${report.banque.taux_endettement > 35 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtPct(report.banque.taux_endettement)}</div>
                            <div className="text-xs text-gray-500 mt-1">Seuil banque : 35%</div>
                          </div>
                          <div className={`p-4 rounded-xl ${report.banque.reste_a_vivre < 1000 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reste à vivre</div>
                            <div className={`text-2xl font-mono font-bold ${report.banque.reste_a_vivre < 1000 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtEUR(report.banque.reste_a_vivre)}</div>
                            <div className="text-xs text-gray-500 mt-1">Après mensualités</div>
                          </div>
                        </div>
                      </div>

                      {/* Repartition chart */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6">
                        <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                          <BarChart3 size={18} className="text-[#c9a227]" />
                          Structure de l'opération
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4 items-center">
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie data={repartitionData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                                {repartitionData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                              </Pie>
                              <Tooltip formatter={(v) => fmtEUR(v)} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between border-b border-gray-100 py-1.5">
                              <span className="text-gray-600">Coût total opération</span>
                              <span className="font-mono font-bold text-[#1e3a5f]">{fmtEUR(report.totaux.cout_total)}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-100 py-1.5">
                              <span className="text-gray-600">Apport</span>
                              <span className="font-mono font-bold text-[#1e3a5f]">{fmtEUR(inputs.apport)}</span>
                            </div>
                            <div className="flex justify-between border-b border-gray-100 py-1.5">
                              <span className="text-gray-600">Montant emprunté</span>
                              <span className="font-mono font-bold text-[#1e3a5f]">{fmtEUR(report.totaux.montant_emprunte)}</span>
                            </div>
                            <div className="flex justify-between py-1.5">
                              <span className="text-gray-600">Cash investi</span>
                              <span className="font-mono font-bold text-[#c9a227]">{fmtEUR(report.totaux.cash_invested)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Comparison */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6">
                        <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1e3a5f] mb-1">
                          Immobilier vs Livret A vs Bourse
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">Capital final après {report.comparaison.duree} ans à apport équivalent</p>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={comparaisonData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="name" fontSize={11} />
                            <YAxis fontSize={10} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => fmtEUR(v)} />
                            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                              {comparaisonData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Amortization */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6">
                        <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1e3a5f] mb-1">
                          Amortissement du prêt
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">Capital vs intérêts année par année</p>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={amortChartData} stackOffset="sign">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="year" fontSize={10} />
                            <YAxis fontSize={10} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => fmtEUR(v)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="Capital" stackId="a" fill="#059669" />
                            <Bar dataKey="Intérêts" stackId="a" fill="#dc2626" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-16 md:py-24 bg-white border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-10 text-center">
              Pourquoi un calculateur "bank-friendly" ?
            </h2>
            <div className="prose max-w-none text-gray-700">
              <p className="text-base leading-relaxed">
                La plupart des calculateurs immobiliers vous donnent un rendement brut et basta. Sauf que votre banquier, lui,
                ne regarde pas le rendement brut. Il regarde votre <strong>taux d'endettement</strong>, votre
                <strong> reste à vivre</strong>, votre <strong>cashflow réel</strong>, et la
                <strong> tenue dans le temps</strong> de votre opération.
              </p>
              <p className="text-base leading-relaxed mt-4">
                Notre outil calcule exactement ce que votre conseiller bancaire calcule pendant que vous lui exposez votre projet.
                Vous arrivez préparé, avec un dossier chiffré, et un PDF qui résume tout : c'est 90% du travail qui vous sépare
                d'un accord de principe.
              </p>

              <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mt-10 mb-4">Les 10 indicateurs essentiels</h3>
              <ul className="space-y-2">
                <li><strong>Rendement brut</strong> : loyer annuel / prix d'achat. Premier filtre.</li>
                <li><strong>Rendement net</strong> : intègre charges, taxe foncière, gestion, vacance.</li>
                <li><strong>Rendement net-net</strong> : après impôts. La vraie performance.</li>
                <li><strong>Cash-on-Cash</strong> : rendement de votre apport. Ce que produit votre cash mobilisé.</li>
                <li><strong>TRI</strong> : performance totale sur 20 ans (cashflow + plus-value + remboursement).</li>
                <li><strong>Cashflow mensuel</strong> : ce qu'il vous reste en poche après tout.</li>
                <li><strong>Mensualité</strong> : à présenter à la banque.</li>
                <li><strong>Coût total du crédit</strong> : transparence sur la dépense long terme.</li>
                <li><strong>Taux d'endettement</strong> : ratio mensualités / revenus. Plafond 35%.</li>
                <li><strong>Reste à vivre</strong> : critère silencieux mais crucial pour les banques.</li>
              </ul>

              <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mt-10 mb-4">FAQ</h3>
              <div className="space-y-5">
                <div>
                  <p className="font-semibold text-[#1e3a5f] mb-1">Mes données sont-elles enregistrées ?</p>
                  <p className="text-sm text-gray-600">Les calculs sont anonymes. Seul le téléchargement du PDF nécessite votre email pour le lead-magnet (et personnaliser le rapport).</p>
                </div>
                <div>
                  <p className="font-semibold text-[#1e3a5f] mb-1">Le calculateur prend-il en compte le LMNP / SCI ?</p>
                  <p className="text-sm text-gray-600">L'impôt est estimé à 30% par défaut. Le rapport PDF inclut des recommandations sur le régime fiscal optimal selon votre cashflow.</p>
                </div>
                <div>
                  <p className="font-semibold text-[#1e3a5f] mb-1">Puis-je l'utiliser pour un projet de division immobilière ?</p>
                  <p className="text-sm text-gray-600">Oui. Saisissez le prix d'achat avant division, l'enveloppe travaux, et le loyer cumulé des lots après division. Vous verrez immédiatement le rendement par rapport à une location classique.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-[#1e3a5f] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(201,162,39,0.3) 0%, transparent 60%)'
          }} />
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-6">
              Votre projet mérite un accompagnement pro
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              G Division rachète directement les biens à valoriser, sans négociation interminable.
              Discutons de votre projet — 30 minutes, sans engagement.
            </p>
            <Link to="/contact">
              <Button className="bg-[#c9a227] text-white hover:bg-[#b08d22] rounded-none px-10 py-6 uppercase tracking-wider text-sm font-semibold">
                Prendre rendez-vous
              </Button>
            </Link>
          </div>
        </section>
      </div>

      {/* Lead modal */}
      <Dialog open={leadOpen} onOpenChange={setLeadOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lead-modal">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl text-[#1e3a5f]">Votre rapport PDF complet</DialogTitle>
            <DialogDescription>
              Gratuit. Tous les calculs + graphiques + recommandations banquier + amortissement complet 20 ans.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm">Prénom & Nom *</Label>
              <Input
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                placeholder="Jean Dupont"
                className="mt-1"
                data-testid="lead-name-input"
              />
            </div>
            <div>
              <Label className="text-sm">Email *</Label>
              <Input
                type="email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                placeholder="jean.dupont@exemple.fr"
                className="mt-1"
                data-testid="lead-email-input"
              />
            </div>
            <div>
              <Label className="text-sm">Téléphone (optionnel)</Label>
              <Input
                type="tel"
                value={leadForm.phone}
                onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                placeholder="06 12 34 56 78"
                className="mt-1"
                data-testid="lead-phone-input"
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={leadForm.consent}
                onChange={(e) => setLeadForm({ ...leadForm, consent: e.target.checked })}
                className="mt-0.5"
              />
              <span>J'accepte de recevoir le rapport et des conseils investissement de G Division. Je peux me désinscrire à tout moment.</span>
            </label>
            <Button
              onClick={handleDownload}
              disabled={downloading || !leadForm.consent}
              className="w-full bg-[#c9a227] hover:bg-[#b08d22] text-white rounded-full py-6 font-bold uppercase tracking-wider text-xs"
              data-testid="download-pdf-btn"
            >
              {downloading ? "Génération en cours…" : "Recevoir mon rapport PDF"}
              <Download size={16} className="ml-2" />
            </Button>
            <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
              <Lock size={11} />
              Document personnalisé, filigrane à votre nom pour traçabilité
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ToolCalculateur;
